import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Modal, Form, Input, Radio, Upload, message, Space, Tooltip, Typography } from 'antd';
import { UploadOutlined, LinkOutlined, PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import io from 'socket.io-client';
// import { useAuth } from '../../context/AuthContext'; // Assuming AuthContext provides user role

const { Text } = Typography;

// Define the backend base URL for file downloads.
// Use same backend as API calls to ensure consistency
const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://care360-backend-dev.onrender.com';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3001' : 'https://care360-backend.onrender.com');

const PatientDocuments = ({ patientId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false); // For Add/Edit Modal
  const [editingDocument, setEditingDocument] = useState(null);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [documentType, setDocumentType] = useState('file'); // 'file' or 'link'

  // State for delete confirmation modal
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  // const { user } = useAuth(); // Get user from AuthContext to check role

  // Socket.IO reference for real-time updates
  const socketRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      let url = `/patients/${patientId}/documents`;
      // Always include deleted documents so users can see them with "Deleted" status
      // and use the built-in filtering to show/hide them as needed
      url += '?includeDeleted=true';
      const response = await axios.get(url); 
      setDocuments(response.data.data || []);
    } catch (error) {
      // console.error("Error fetching documents:", error.response?.data?.error || error.message); // Keep this for debugging if needed
      message.error('Failed to fetch documents: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [patientId]); 

  // Handle view/download with proper error handling
  const handleViewDownload = async (url, record) => {
    try {
      if (record.documentType === 'link') {
        // For external links, open directly
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        // For files, check if URL is accessible first
        const response = await fetch(url, { method: 'HEAD' });
        if (response.ok) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          throw new Error(`File not accessible (${response.status})`);
        }
      }
    } catch (error) {
      console.error('Error accessing document:', error);
      message.error(`Failed to open document: ${error.message || 'File may not exist or access denied'}`);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Socket.IO setup for real-time document updates
  useEffect(() => {
    if (!patientId) return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      console.warn('PatientDocuments: No auth token found for socket connection.');
      return;
    }

    // Initialize socket connection
    socketRef.current = io(SOCKET_URL, {
      auth: { token }
    });

    socketRef.current.on('connect', () => {
      console.log('PatientDocuments: Connected to Socket.IO server');
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('PatientDocuments: Disconnected from Socket.IO server:', reason);
    });

    socketRef.current.on('connect_error', (err) => {
      console.error('PatientDocuments: Socket connection error:', err.message);
    });

    // Listen for document upload events
    socketRef.current.on('documentUploaded', (data) => {
      console.log('PatientDocuments: Document uploaded event received:', data);

      // Only refresh if the document belongs to the current patient
      if (data.patientId === patientId) {
        console.log('PatientDocuments: Refreshing document list due to new upload');
        fetchDocuments();

        // Show a notification about the new document
        if (data.source && data.source.startsWith('care-planning')) {
          message.success(`New document "${data.documentName}" added from Care Planning!`, 3);
        }
      }
    });

    // Cleanup on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [patientId, fetchDocuments]);

  const handleAddDocument = () => {
    setEditingDocument(null);
    setDocumentType('file');
    form.resetFields();
    form.setFieldsValue({ documentType: 'file' });
    setFileList([]);
    setIsModalVisible(true);
  };

  const handleEditDocument = (record) => {
    if (record.isDeleted) {
      message.info("Cannot edit a deleted document's metadata.");
      return;
    }
    setEditingDocument(record);
    setDocumentType(record.documentType);
    form.setFieldsValue({
      ...record,
      uploadDate: record.uploadDate ? moment(record.uploadDate) : null,
    });
    if (record.documentType === 'file') {
      // Don't set fileList with existing file to allow user to upload new file
      // The current file info is shown in the UI text below the upload button
      setFileList([]);
    } else {
      setFileList([]);
    }
    setIsModalVisible(true);
  };

  const showDeleteConfirmModal = (record) => {
    setDocumentToDelete(record);
    setIsDeleteConfirmVisible(true);
  };

  const handleDeleteConfirmOk = async () => {
    if (documentToDelete && documentToDelete._id) {
      await handleDeleteDocument(documentToDelete._id);
    } else {
      // console.error('[FE] handleDeleteConfirmOk: documentToDelete or its _id is null/undefined'); // Keep for debugging if needed
      message.error('Error: Document to delete is not properly set.');
    }
    setIsDeleteConfirmVisible(false);
    setDocumentToDelete(null);
  };

  const handleDeleteConfirmCancel = () => {
    setIsDeleteConfirmVisible(false);
    setDocumentToDelete(null);
  };

  const handleDeleteDocument = async (documentId) => {
    try {
      await axios.delete(`/documents/${documentId}`); 
      message.success('Document marked as deleted successfully.');
      fetchDocuments(); 
    } catch (error) {
      // console.error("DETAILED Error deleting document:", error); // Keep for debugging if needed
      // if (error.response) {
      //   console.error("Error response data:", error.response.data);
      //   console.error("Error response status:", error.response.status);
      //   console.error("Error response headers:", error.response.headers);
      // } else if (error.request) {
      //   console.error("Error request:", error.request);
      // } else {
      //   console.error('Error message:', error.message);
      // }
      message.error(`Failed to delete document: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleModalOk = async () => { // For Add/Edit Modal
    console.log('🔍 PDF Upload Debug - Starting handleModalOk');
    console.log('🔍 fileList:', fileList);
    console.log('🔍 fileList[0]:', fileList[0]);
    console.log('🔍 fileList[0] instanceof File:', fileList[0] instanceof File);
    console.log('🔍 editingDocument:', editingDocument);

    let formData = null; // Declare in function scope for debugging

    try {
      const values = await form.validateFields();
      console.log('🔍 Form values:', values);
      formData = new FormData();
      formData.append('documentName', values.documentName);
      formData.append('documentType', values.documentType);
      formData.append('description', values.description || '');
      const userId = editingDocument ? editingDocument.uploadedBy._id : ( 'mockUserId123AFakeOne' );
      formData.append('uploadedBy', userId);

      if (values.documentType === 'file') {
        if (!editingDocument && fileList.length > 0 && fileList[0]) {
          if (!(fileList[0] instanceof File)) { 
            message.error('Selected item is not a valid file. Please try selecting the file again.');
            return;
          }
          formData.append('file', fileList[0]);
        } else if (!editingDocument && fileList.length === 0) {
          message.error('Please select a file to upload.');
          return;
        }
      } else { 
        formData.append('externalLink', values.externalLink);
      }
      
      // Log FormData contents before sending
      console.log('🔍 FormData contents before sending:');
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}:`, value);
      }

      setLoading(true);
      if (editingDocument) {
        if (values.documentType === 'file' && fileList.length > 0 && fileList[0] instanceof File) {
          // User wants to update the file - use FormData for file upload
          const updateFormData = new FormData();
          updateFormData.append('documentName', values.documentName);
          updateFormData.append('description', values.description || '');
          updateFormData.append('file', fileList[0]);
          
          await axios.put(`/documents/${editingDocument._id}`, updateFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          message.success('Document updated successfully.');
        } else if (values.documentType === 'link') {
          // Update external link
          await axios.put(`/documents/${editingDocument._id}`, { 
            documentName: values.documentName,
            description: values.description,
            externalLink: values.externalLink
          });
          message.success('Document updated successfully.');
        } else {
          // Only metadata update (no file change)
          await axios.put(`/documents/${editingDocument._id}`, { 
            documentName: values.documentName,
            description: values.description,
          });
          message.success('Document metadata updated successfully.');
        }
      } else {
        await axios.post(`/patients/${patientId}/documents`, formData, { 
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Document added successfully.');
      }
      setIsModalVisible(false);
      fetchDocuments(); 
    } catch (error) {
      console.error("🔴 PDF Upload Error Debug:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: error.config,
        message: error.message,
        stack: error.stack
      });

      // Log the FormData contents for debugging
      if (formData) {
        console.error("🔴 FormData contents:");
        for (let [key, value] of formData.entries()) {
          console.error(`  ${key}:`, value);
        }
      }

      // Note: For edit mode with file upload, separate updateFormData is used
      console.error("🔴 Error occurred in:", editingDocument ? "Edit mode" : "Add mode");

      message.error(`Failed to save document: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => { // For Add/Edit Modal
    setIsModalVisible(false);
  };

  const onDocumentTypeChange = (e) => {
    setDocumentType(e.target.value);
    form.setFieldsValue({ documentType: e.target.value });
    if (e.target.value === 'link') {
      form.setFieldsValue({ documentFile: null }); 
      setFileList([]);
    }
  };

  const uploadProps = {
    onRemove: () => { setFileList([]); },
    beforeUpload: (file) => {
      // Enhanced validation with debugging for PDF upload issues
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'video/mp4', 'video/avi', 'video/mov', 'video/quicktime'];
      const maxSize = 100 * 1024 * 1024; // 100MB

      // Debug logging for PDF upload issues (remove in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('File upload debug:', {
          name: file.name,
          type: file.type,
          size: file.size,
          extension: file.name?.split('.').pop()?.toLowerCase()
        });
      }

      // Enhanced PDF type checking - some browsers may send different MIME types
      const isPDF = file.type === 'application/pdf' ||
                   file.name?.toLowerCase().endsWith('.pdf') ||
                   file.type === 'application/x-pdf' ||
                   file.type === 'application/acrobat' ||
                   file.type === 'applications/vnd.pdf' ||
                   file.type === 'text/pdf' ||
                   file.type === 'text/x-pdf';

      const isAllowedType = allowedTypes.includes(file.type) || isPDF;

      if (!isAllowedType) {
        console.error('File type rejected:', file.type, 'for file:', file.name);
        message.error(`File type not supported: ${file.type}. Allowed types: Images (JPG, PNG, GIF), Documents (PDF, DOC, DOCX), Videos (MP4, AVI, MOV)`);
        return false;
      }

      if (file.size > maxSize) {
        message.error('File size too large. Maximum size allowed is 50MB.');
        return false;
      }
      
      setFileList([file]); 
      return false; 
    }, 
    fileList,
    maxCount: 1,
    accept: '.jpg,.jpeg,.png,.gif,.pdf,application/pdf,.doc,.docx,.mp4,.avi,.mov'
  };

  const columns = [
    {
      title: 'Document Name',
      dataIndex: 'documentName',
      key: 'documentName',
      render: (text, record) => {
        const style = record.isDeleted ? { textDecoration: 'line-through', color: 'grey' } : {};
        let url;
        if (record.documentType === 'link') {
          url = record.externalLink;
          return <a href={url} target="_blank" rel="noopener noreferrer" style={style}>{text} <LinkOutlined /></a>;
        }
        // Use the document service endpoint for file access with authentication token
        const token = localStorage.getItem('authToken');
        url = `${BACKEND_URL}/patients/${patientId}/documents/file/${record._id}${token ? `?token=${token}` : ''}`;

        // Debug logging for file access
        console.log(`[DocumentLink] Accessing file: ${record.documentName}`, {
          documentId: record._id,
          fileUrl: record.fileUrl,
          fileName: record.fileName,
          mimeType: record.mimeType,
          fullUrl: url,
          uploadDate: record.uploadDate
        });

        return <a href={url} target="_blank" rel="noopener noreferrer" style={style}>{text}</a>;
      }
    },
    {
      title: 'Type',
      dataIndex: 'documentType',
      key: 'documentType',
      render: (type) => type.charAt(0).toUpperCase() + type.slice(1), 
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: false,
      width: 200,
      render: (text) => (
        <div style={{
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
          maxWidth: '200px'
        }}>
          {text || '-'}
        </div>
      )
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      render: (user) => user ? (user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim()) : 'N/A',
    },
    {
      title: 'Upload Date',
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      render: (date) => moment(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a, b) => moment(a.uploadDate).unix() - moment(b.uploadDate).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Status',
      dataIndex: 'isDeleted',
      key: 'status',
      render: (isDeleted) => isDeleted ? <Text type="danger" italic>Deleted</Text> : <Text type="success">Active</Text>,
      filters: [
        { text: 'Active', value: false },
        { text: 'Deleted', value: true },
      ],
      onFilter: (value, record) => record.isDeleted === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isDeleted = record.isDeleted;
        let downloadViewUrl = '';
        if (record.documentType === 'file') {
          // Use the document service endpoint for file access with authentication token
          const token = localStorage.getItem('authToken');
          downloadViewUrl = `${BACKEND_URL}/patients/${patientId}/documents/file/${record._id}${token ? `?token=${token}` : ''}`;
        } else if (record.documentType === 'link') {
          downloadViewUrl = record.externalLink;
        }

        return (
          <Space size="middle">
            <Tooltip title={isDeleted ? "Cannot edit deleted document" : "Edit Metadata"}>
              <Button icon={<EditOutlined />} onClick={() => handleEditDocument(record)} disabled={isDeleted} />
            </Tooltip>

            {!isDeleted ? (
              <Tooltip title="Delete Document">
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => showDeleteConfirmModal(record)}
                />
              </Tooltip>
            ) : (
              null
            )}

            {(record.documentType === 'file' || record.documentType === 'link') && downloadViewUrl && (
              <Tooltip title={record.documentType === 'file' ? "Download/View File" : "Open Link"}>
                <Button
                  icon={record.documentType === 'file' ? <DownloadOutlined /> : <LinkOutlined />}
                  onClick={() => handleViewDownload(downloadViewUrl, record)}
                />
              </Tooltip>
            )}
          </Space>
        );
      }
    },
  ];

  return (
    <div>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleAddDocument}
        style={{ marginBottom: 16 }}
      >
        Add Document
      </Button>
      <Table
        columns={columns}
        dataSource={documents}
        loading={loading}
        rowKey="_id"
        scroll={{ x: true }}
      />
      {/* Add/Edit Modal */}
      <Modal
        title={editingDocument ? 'Edit Document Metadata' : 'Add New Document'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        destroyOnClose
        width={600}
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto'
          }
        }}
      >
        <Form form={form} layout="vertical" name="documentForm">
          {/* Form items for add/edit */}
          <Form.Item name="documentType" label="Document Type" rules={[{ required: true, message: 'Please select document type!' }]} initialValue={documentType}>
            <Radio.Group onChange={onDocumentTypeChange}>
              <Radio.Button value="file">Upload File</Radio.Button>
              <Radio.Button value="link">External Link</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="documentName" label="Document Name" rules={[{ required: true, message: 'Please enter the document name!' }]}>
            <Input placeholder="e.g., Cardiology Report 2023-10-15" style={{ wordBreak: 'break-all' }} />
          </Form.Item>
          <Form.Item name="description" label="Description (Optional)">
            <Input.TextArea
              rows={3}
              placeholder="Brief description of the document"
              style={{
                whiteSpace: 'pre-wrap !important',
                wordWrap: 'break-word !important',
                wordBreak: 'break-word !important',
                overflowWrap: 'break-word !important',
                width: '100% !important',
                minWidth: '0 !important',
                maxWidth: '100% !important',
                resize: 'vertical',
                boxSizing: 'border-box'
              }}
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </Form.Item>
          {form.getFieldValue('documentType') === 'file' && (
            <Form.Item name="documentFile" label="File" rules={[{ required: !editingDocument && fileList.length === 0, message: 'Please upload a file!' }]}>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>
                  {editingDocument && documentType === 'file' ? 
                    (fileList[0]?.name || `Current: ${editingDocument.fileName || 'Unknown file'} (Click to change)`) : 
                    'Click to Upload'
                  }
                </Button>
              </Upload>
              {editingDocument && documentType === 'file' && fileList.length === 0 && <Text type="secondary" style={{display: 'block', marginTop: '8px', wordBreak: 'break-all'}}>Current file: {editingDocument.fileName || 'Unknown file'}</Text>}
              {editingDocument && documentType === 'file' && fileList.length > 0 && <Text type="warning" style={{display: 'block', marginTop: '8px', wordBreak: 'break-all'}}>New file selected: {fileList[0]?.name} (will replace current file)</Text>}
            </Form.Item>
          )}
          {form.getFieldValue('documentType') === 'link' && (
            <Form.Item name="externalLink" label="External Link URL" rules={[{ required: true, message: 'Please enter the external link URL!' },{ type: 'url', message: 'Please enter a valid URL!' }]}>
              <Input placeholder="https://example.com/document.pdf" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={isDeleteConfirmVisible}
        onOk={handleDeleteConfirmOk}
        onCancel={handleDeleteConfirmCancel}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={loading} // You might want a separate loading state for delete if it's slow
      >
        <p>Are you sure you want to delete the document: "{documentToDelete?.documentName || 'this document'}"?</p>
        <p>This action will mark the document as deleted.</p>
      </Modal>
    </div>
  );
};

export default PatientDocuments;
